import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { TokenProvider } from './contexts/TokenContext';
import MainLayout from './components/layout/MainLayout';
import HomePage from './pages/HomePage';
import TravelDesignerDashboard from './pages/TravelDesignerDashboard';
import CreateItineraryPage from './pages/CreateItineraryPage';
import PlannerPage from './pages/PlannerPage';
import AtlasFilesPage from './pages/AtlasFilesPage';
import AtlasFileEditorPage from './pages/AtlasFileEditorPage';
import AtlasFileViewPage from './pages/AtlasFileViewPage';
import ProfilePage from './pages/ProfilePage';
import ItineraryViewPage from './pages/ItineraryViewPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AtlasPlanPage from './pages/AtlasPlanPage';
import CookieConsent from './components/ui/CookieConsent';
import { trackPageView } from './utils/metaPixel';

// React Router doesn't reset scroll position on navigation (that's a full-page-load
// behaviour browsers give you for free, not something an SPA router does). Without
// this, clicking a link from partway down a tall page lands on the new page at the
// same pixel offset — which can easily be its footer.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// The pixel only auto-fires PageView on load; virtual navigations need one each.
function PixelPageViews() {
  const { pathname } = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    trackPageView();
  }, [pathname]);
  return null;
}

function App() {
  return (
    <Router>
    <AuthProvider>
    <TokenProvider>
      <ScrollToTop />
      <PixelPageViews />
      <Routes>
        {/* Pages with MainLayout */}
        <Route path="/" element={<MainLayout><HomePage /></MainLayout>} />
        <Route path="/designer" element={<MainLayout><TravelDesignerDashboard /></MainLayout>} />
        <Route path="/designer/create" element={<MainLayout><CreateItineraryPage /></MainLayout>} />
        <Route path="/atlas" element={<MainLayout><AtlasFilesPage /></MainLayout>} />
        <Route path="/atlas/new" element={<MainLayout><AtlasFileEditorPage /></MainLayout>} />
        <Route path="/atlas/edit/:id" element={<MainLayout><AtlasFileEditorPage /></MainLayout>} />
        <Route path="/atlas/:id" element={<MainLayout><AtlasFileViewPage /></MainLayout>} />
        <Route path="/profile" element={<MainLayout><ProfilePage /></MainLayout>} />
        <Route path="/itinerary/:id" element={<MainLayout><ItineraryViewPage /></MainLayout>} />
        <Route path="/login" element={<MainLayout><LoginPage /></MainLayout>} />
        <Route path="/register" element={<MainLayout><RegisterPage /></MainLayout>} />
        <Route path="/forgot-password" element={<MainLayout><ForgotPasswordPage /></MainLayout>} />
        <Route path="/reset-password" element={<MainLayout><ResetPasswordPage /></MainLayout>} />
        <Route path="/auth/callback" element={<MainLayout><AuthCallbackPage /></MainLayout>} />
        <Route path="/admin" element={<MainLayout><AdminDashboardPage /></MainLayout>} />
        <Route path="/contact" element={<MainLayout><HomePage /></MainLayout>} />
        <Route path="/atlas-plan" element={<MainLayout><AtlasPlanPage /></MainLayout>} />
        {/* Legacy paths now unified under Atlas Plan */}
        <Route path="/atlas-expert" element={<Navigate to="/atlas-plan" replace />} />
        <Route path="/concierge" element={<Navigate to="/atlas-plan" replace />} />
        <Route path="/pricing" element={<Navigate to="/atlas-plan" replace />} />

        {/* Planner page without MainLayout (full screen) */}
        <Route path="/designer/planner/:id" element={<PlannerPage />} />
        <Route path="/designer/edit/:id" element={<PlannerPage />} />
      </Routes>
      <CookieConsent />
    </TokenProvider>
    </AuthProvider>
    </Router>
  );
}

export default App;
